/**
 * User Settings Page - Benutzerverwaltung
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Badge as BadgeIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  LockReset as LockResetIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import { useAuth } from "../../context/AuthContext";
import { apiClient } from "../../services/apiClient";
import { User, UserRole } from "../../types/api-types";

interface UserFormData {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: UserRole;
  isActive: boolean;
  password: string;
  newPassword: string;
  confirmPassword: string;
}

interface UserFormSubmitData {
  username: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  role?: UserRole;
  isActive?: boolean;
  password?: string;
  newPassword?: string;
}

const roleLabels: Record<UserRole, string> = {
  [UserRole.ADMIN]: "Administrator",
  [UserRole.MANAGER]: "Manager",
  [UserRole.CASHIER]: "Kassierer",
  [UserRole.VIEWER]: "Betrachter",
};

const UserSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  const isManager = currentUser?.role === UserRole.MANAGER;

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({
    open: false,
    user: null,
  });

  const canManageUser = (targetUser: User): boolean => {
    if (isAdmin) {
      return true;
    }

    return Boolean(isManager && targetUser.role === UserRole.CASHIER);
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await apiClient.getUsers();
      const activeUsers = userList.filter((loadedUser) => loadedUser.isActive);
      setUsers(
        isManager && !isAdmin
          ? activeUsers.filter((loadedUser) => loadedUser.role === UserRole.CASHIER)
          : activeUsers
      );
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role]);

  const handleEditUser = (targetUser: User) => {
    if (!canManageUser(targetUser)) {
      setError("Sie haben keine Berechtigung, diesen Benutzer zu bearbeiten.");
      return;
    }

    setSelectedUser(targetUser);
    setShowForm(true);
  };

  const handleAddUser = () => {
    if (!isAdmin) {
      setError("Nur Administratoren können neue Benutzer anlegen.");
      return;
    }

    setSelectedUser(null);
    setShowForm(true);
  };

  const handleDeleteUser = (targetUser: User) => {
    if (!isAdmin) {
      setError("Nur Administratoren können Benutzer löschen.");
      return;
    }

    setDeleteDialog({ open: true, user: targetUser });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.user) return;

    try {
      await apiClient.deleteUser(deleteDialog.user.id);
      setUsers(users.filter((user) => user.id !== deleteDialog.user!.id));
      setDeleteDialog({ open: false, user: null });
    } catch (err: any) {
      setError(err.message || "Fehler beim Löschen des Benutzers");
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return "error";
      case UserRole.MANAGER:
        return "warning";
      case UserRole.CASHIER:
        return "primary";
      default:
        return "default";
    }
  };

  const getDisplayName = (targetUser: User) => {
    const fullName = [targetUser.firstName, targetUser.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || targetUser.username;
  };

  if (loading) {
    return <LoadingSpinner text="Lade Benutzer..." />;
  }

  if (showForm) {
    return (
      <UserForm
        user={selectedUser}
        canEditAdministrativeFields={isAdmin}
        canSetPassword={Boolean(isAdmin || isManager)}
        onSave={async (userData) => {
          try {
            setSaving(true);

            const { newPassword, ...profileData } = userData;
            if (selectedUser) {
              await apiClient.updateUser(selectedUser.id, profileData);
              if (newPassword) {
                await apiClient.resetUserPassword(selectedUser.id, {
                  newPassword,
                });
              }
            } else {
              await apiClient.createUser(profileData as any);
            }

            await loadUsers();
            setShowForm(false);
            setSelectedUser(null);
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern des Benutzers");
          } finally {
            setSaving(false);
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedUser(null);
        }}
        saving={saving}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <PeopleIcon fontSize={isMobile ? "medium" : "large"} />
              Benutzerverwaltung
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {isManager && !isAdmin
                ? "Kassiererprofile und Passwörter verwalten"
                : "Benutzerkonten, Berechtigungen und Passwörter verwalten"}
            </Typography>
          </Box>

          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              fullWidth={isMobile}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                borderRadius: 2,
              }}
            >
              Neuer Benutzer
            </Button>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {users.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <PeopleIcon
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
          >
            {isManager && !isAdmin
              ? "Keine Kassierer gefunden"
              : "Keine Benutzer gefunden"}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {isAdmin
              ? "Erstellen Sie den ersten Benutzer, um mit der Verwaltung zu beginnen."
              : "Aktuell sind keine Kassierer zur Verwaltung verfügbar."}
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              sx={{
                mt: 3,
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Ersten Benutzer anlegen
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {users.map((targetUser) => (
            <Grid item xs={12} md={6} lg={4} key={targetUser.id}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: { xs: "none", md: "translateY(-4px)" },
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {getDisplayName(targetUser)}
                    </Typography>
                  </Box>

                  <Stack spacing={1.25}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EmailIcon
                        sx={{ mr: 1, fontSize: 18, color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {targetUser.email}
                      </Typography>
                    </Box>

                    {targetUser.phoneNumber && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon
                          sx={{ mr: 1, fontSize: 18, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {targetUser.phoneNumber}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={roleLabels[targetUser.role]}
                        color={getRoleColor(targetUser.role)}
                        size="small"
                        variant="outlined"
                      />
                      {!targetUser.isActive && (
                        <Chip
                          label="Inaktiv"
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Stack>
                </CardContent>

                <Divider />

                <CardActions
                  sx={{
                    p: { xs: 2, md: 3 },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditUser(targetUser)}
                    variant="outlined"
                    fullWidth={isMobile}
                  >
                    Bearbeiten
                  </Button>
                  {isAdmin && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(targetUser)}
                      sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Benutzer löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Benutzer "{deleteDialog.user && getDisplayName(deleteDialog.user)}"
            wirklich löschen? Diese Aktion deaktiviert das Konto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>
            Abbrechen
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

interface UserFormProps {
  user: User | null;
  canEditAdministrativeFields: boolean;
  canSetPassword: boolean;
  onSave: (user: UserFormSubmitData) => Promise<void> | void;
  onCancel: () => void;
  saving: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  canEditAdministrativeFields,
  canSetPassword,
  onSave,
  onCancel,
  saving,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: user?.username || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    role: user?.role || UserRole.CASHIER,
    isActive: user?.isActive !== false,
    password: "",
    newPassword: "",
    confirmPassword: "",
  });

  const setField = <K extends keyof UserFormData>(
    field: K,
    value: UserFormData[K]
  ) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (formData.username.trim().length < 2) {
      return "Der Benutzername muss mindestens 2 Zeichen lang sein.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    }

    if (!user && formData.password.length < 8) {
      return "Das Startpasswort muss mindestens 8 Zeichen lang sein.";
    }

    if (user && (formData.newPassword || formData.confirmPassword)) {
      if (formData.newPassword.length < 8) {
        return "Das neue Passwort muss mindestens 8 Zeichen lang sein.";
      }

      if (formData.newPassword !== formData.confirmPassword) {
        return "Die Passwortbestätigung stimmt nicht überein.";
      }
    }

    return null;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const submitData: UserFormSubmitData = {
      username: formData.username.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
    };

    if (canEditAdministrativeFields) {
      submitData.role = formData.role;
      submitData.isActive = formData.isActive;
    }

    if (!user) {
      submitData.password = formData.password;
      submitData.role = formData.role;
      submitData.isActive = formData.isActive;
    }

    if (user && canSetPassword && formData.newPassword) {
      submitData.newPassword = formData.newPassword;
    }

    onSave(submitData);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: "bold",
          }}
        >
          <PeopleIcon fontSize={isMobile ? "medium" : "large"} />
          {user ? "Benutzer bearbeiten" : "Neuer Benutzer"}
        </Typography>
      </Paper>

      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <Box component="form" onSubmit={handleSubmit}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={3}>
              {formError && (
                <Alert severity="error" onClose={() => setFormError(null)}>
                  {formError}
                </Alert>
              )}

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Profildaten
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stammdaten für Anmeldung, Kontakt und Anzeige.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vorname"
                    value={formData.firstName}
                    onChange={(event) => setField("firstName", event.target.value)}
                    variant="outlined"
                    autoComplete="given-name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nachname"
                    value={formData.lastName}
                    onChange={(event) => setField("lastName", event.target.value)}
                    variant="outlined"
                    autoComplete="family-name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Benutzername"
                    value={formData.username}
                    onChange={(event) => setField("username", event.target.value)}
                    required
                    variant="outlined"
                    autoComplete="username"
                    InputProps={{
                      startAdornment: <BadgeIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    type="email"
                    value={formData.email}
                    onChange={(event) => setField("email", event.target.value)}
                    required
                    variant="outlined"
                    autoComplete="email"
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={formData.phoneNumber}
                    onChange={(event) => setField("phoneNumber", event.target.value)}
                    variant="outlined"
                    autoComplete="tel"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                  />
                </Grid>

                {canEditAdministrativeFields ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Rolle"
                        value={formData.role}
                        onChange={(event) =>
                          setField("role", event.target.value as UserRole)
                        }
                        variant="outlined"
                        SelectProps={{ native: true }}
                      >
                        <option value={UserRole.CASHIER}>Kassierer</option>
                        <option value={UserRole.MANAGER}>Manager</option>
                        <option value={UserRole.ADMIN}>Administrator</option>
                        <option value={UserRole.VIEWER}>Betrachter</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Status"
                        value={formData.isActive ? "active" : "inactive"}
                        onChange={(event) =>
                          setField("isActive", event.target.value === "active")
                        }
                        variant="outlined"
                        SelectProps={{ native: true }}
                      >
                        <option value="active">Aktiv</option>
                        <option value="inactive">Inaktiv</option>
                      </TextField>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Rolle und Status bleiben unverändert.
                    </Alert>
                  </Grid>
                )}
              </Grid>

              <Divider />

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <LockResetIcon fontSize="small" />
                  Passwort
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user
                    ? "Optional ein neues Passwort für diesen Benutzer setzen."
                    : "Startpasswort für den neuen Benutzer festlegen."}
                </Typography>
              </Box>

              {!user ? (
                <TextField
                  fullWidth
                  label="Passwort"
                  type="password"
                  value={formData.password}
                  onChange={(event) => setField("password", event.target.value)}
                  required
                  variant="outlined"
                  autoComplete="new-password"
                />
              ) : (
                canSetPassword && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Neues Passwort"
                        type="password"
                        value={formData.newPassword}
                        onChange={(event) =>
                          setField("newPassword", event.target.value)
                        }
                        variant="outlined"
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Passwort bestätigen"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(event) =>
                          setField("confirmPassword", event.target.value)
                        }
                        variant="outlined"
                        autoComplete="new-password"
                      />
                    </Grid>
                  </Grid>
                )
              )}
            </Stack>
          </CardContent>

          <Divider />

          <CardActions
            sx={{
              p: { xs: 2, md: 3 },
              justifyContent: "flex-end",
              gap: 2,
              flexDirection: { xs: "column-reverse", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={saving}
              fullWidth={isMobile}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              fullWidth={isMobile}
            >
              {saving ? "Speichere..." : "Speichern"}
            </Button>
          </CardActions>
        </Box>
      </Card>
    </Container>
  );
};

export default UserSettingsPage;
